import { Fragment } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import {
  HomeIcon,
  CalendarIcon,
  CreditCardIcon,
  DocumentIcon,
  PlusCircleIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import authStore from '../../store/authStore';

const nav = [
  { name: 'Home',         to: '/portal',              icon: HomeIcon,                   end: true },
  { name: 'Appointments', to: '/portal/appointments', icon: CalendarIcon },
  { name: 'Schedule',     to: '/portal/schedule',     icon: PlusCircleIcon },
  { name: 'Messages',     to: '/portal/messages',     icon: ChatBubbleLeftRightIcon },
  { name: 'Invoices',     to: '/portal/invoices',     icon: CreditCardIcon },
  { name: 'Documents',    to: '/portal/documents',    icon: DocumentIcon },
  { name: 'Forms',        to: '/portal/intake-forms', icon: ClipboardDocumentListIcon },
];

// First 5 nav items shown in mobile bottom bar
const mobileNav = nav.slice(0, 5);

function getInitials(user) {
  return `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase();
}

export default function PortalLayout() {
  const { user, logout } = authStore();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="portal min-h-screen bg-neutral-50 dark:bg-neutral-950 pb-16 md:pb-0">
      {/* Header */}
      <header className="bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700 px-4 sm:px-6 h-14 flex items-center">
        <div className="max-w-4xl mx-auto w-full flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-[var(--portal-primary-600,#0d9488)]">Sage</span>
            <span className="hidden sm:block text-sm text-neutral-400 dark:text-neutral-500">Patient Portal</span>
          </div>

          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
              <div className="w-7 h-7 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-400 flex items-center justify-center text-xs font-semibold">
                {getInitials(user)}
              </div>
              <span className="hidden sm:block text-sm text-neutral-700 dark:text-neutral-300">
                {user?.firstName}
              </span>
              <ChevronDownIcon className="w-3.5 h-3.5 text-neutral-400 hidden sm:block" />
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-1 w-48 rounded-xl bg-white dark:bg-neutral-800 shadow-lg border border-neutral-200 dark:border-neutral-700 focus:outline-none z-50">
                <div className="px-3 py-2.5 border-b border-neutral-100 dark:border-neutral-700">
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-neutral-500 truncate">{user?.email}</p>
                </div>
                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        className={`w-full text-left px-3 py-2 text-sm rounded-lg mx-0.5 transition-colors
                          ${active ? 'bg-neutral-100 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100' : 'text-neutral-700 dark:text-neutral-300'}`}
                      >
                        Sign out
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </header>

      {/* Desktop pill-segment nav */}
      <div className="hidden md:block bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-2">
          <nav className="flex gap-1 p-1 bg-neutral-100 dark:bg-neutral-800 rounded-xl w-fit">
            {nav.map((item) => (
              <NavLink
                key={item.name}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive
                      ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 shadow-xs'
                      : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                  }`
                }
              >
                <item.icon className="h-4 w-4 shrink-0" />
                {item.name}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <Outlet />
      </main>

      {/* Mobile bottom navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-700 z-30">
        <div className="flex">
          {mobileNav.map((item) => (
            <NavLink
              key={item.name}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center gap-1 py-2 text-[10px] font-medium transition-colors ${
                  isActive
                    ? 'text-teal-600 dark:text-teal-400'
                    : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.name}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
